import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, MessageCircle } from "lucide-react";
import PublicHeader from "@/components/public/PublicHeader";
import PublicFooter from "@/components/public/PublicFooter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { trackLead } from "@/lib/pixel";

const STEPS = ["contacto", "precalificacion", "solicitud", "confirmacion"];
const WA_DEFAULT = `https://wa.me/529999980648?text=${encodeURIComponent("Ya ingresé mi solicitud en Dupla Automotriz, voy a enviar mis documentos.")}`;

/* Borrador guardado en el navegador para no perder respuestas si el cliente sale */
function useDraft(key, initial) {
  const [v, setV] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? { ...initial, ...JSON.parse(s) } : initial; } catch { return initial; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* sin almacenamiento */ } }, [key, v]);
  const clear = () => { try { localStorage.removeItem(key); } catch { /* noop */ } };
  return [v, setV, clear];
}

export default function Buy() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(STEPS[0]);
  const [token, setToken] = useState(null);
  const [buyerName, setBuyerName] = useState("");
  const [whatsapp, setWhatsapp] = useState(WA_DEFAULT);
  const [resumeError, setResumeError] = useState("");

  useEffect(() => {
    const token = params.get("token");
    const legacy = params.get("continuar");
    if (!token && !legacy) { return; }
    const url = token ? `/buyers/public/by-token/${encodeURIComponent(token)}` : `/buyers/public/${legacy}`;
    api.get(url).then(({ data }) => {
      setToken(data.token);
      setBuyerName(data.nombre || "");
      if (data.application_completed) setStep("confirmacion");
      else if (data.prequalification_completed) setStep("solicitud");
      else setStep("precalificacion");
    }).catch((err) => {
      const msg = err?.response?.status === 410 ? "Este enlace ya expiró. Escríbenos por WhatsApp y te mandamos uno nuevo." : "Este enlace no es válido.";
      setResumeError(msg);
      toast.error(msg);
    });
  }, [params]);

  const go = (name) => { toast.dismiss(); setStep(name); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="min-h-screen bg-white text-[#3E5641]" data-testid="page-buy">
      <PublicHeader />
      <main className="container-x pt-28 pb-20 md:pt-40 md:pb-24">
        {step !== "duplicado" && <ProgressBar step={step} />}
        {resumeError && step === "contacto" && (
          <p className="mb-6 rounded-sm border-l-4 border-[#C4674F] bg-[#F1F3F3] p-4 text-sm" data-testid="buy-resume-error">{resumeError}</p>
        )}
        {step === "contacto" && <StepContacto onDone={(tk, nombre) => { setBuyerName(nombre); if (!tk) { go("duplicado"); return; } setToken(tk); go("precalificacion"); }} />}
        {step === "duplicado" && <StepDuplicado nombre={buyerName} />}
        {step === "precalificacion" && <StepPrecalif token={token} onDone={() => go("solicitud")} />}
        {step === "solicitud" && <StepSolicitud token={token} onDone={(wa) => { if (wa) setWhatsapp(wa); go("confirmacion"); }} />}
        {step === "confirmacion" && <StepConfirmacion nombre={buyerName} whatsappLink={whatsapp} />}
      </main>
      <PublicFooter />
    </div>
  );
}

function ProgressBar({ step }) {
  const idx = Math.max(STEPS.indexOf(step), 0);
  const pct = ((idx + 1) / STEPS.length) * 100;
  return (
    <div className="mb-8 md:mb-14" data-testid="buy-progress">
      <div className="mb-2 flex justify-between text-[10px] mono uppercase tracking-widest text-[#6B7678]">
        <span>Paso {idx + 1} de {STEPS.length}</span>
        <span>{Math.round(pct)}%</span>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-[#F1F3F3]">
        <div className="h-full bg-[#C4674F] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/* ================ Paso 1: Contacto ================ */
function StepContacto({ onDone }) {
  const [form, setForm] = useState({
    nombre: "", telefono: "", correo: "", ciudad: "", tiene_auto_en_mente: "no", tipo_unidad: "",
    marca: "", anio: "", modelo: "", version: "", transmision: "", rango_precio: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const tieneAuto = form.tiene_auto_en_mente === "si";

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        nombre: form.nombre.trim(), telefono: form.telefono.trim(),
        correo: form.correo.trim(), ciudad: form.ciudad.trim(),
        tiene_auto_en_mente: tieneAuto,
        tipo_unidad: form.tipo_unidad || "Sin definir",
      };
      if (tieneAuto) payload.auto_en_mente = {
        marca: form.marca, anio: form.anio, modelo: form.modelo,
        version: form.version, transmision: form.transmision, rango_precio: form.rango_precio,
      };
      const { data } = await api.post("/buyers", payload);
      trackLead({ content_name: "Comprar un auto", content_category: "buy" });
      onDone(data.duplicate ? null : data.token, form.nombre);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="grid gap-8 md:grid-cols-12 md:gap-16">
      <div className="md:col-span-5">
        <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Comprar un auto</p>
        <h1 className="mt-3 font-serif text-4xl text-[#3E5641] md:text-5xl">Empieza tu precalificación.</h1>
        <p className="mt-5 max-w-md text-[#6B7678]">
          Regístrate y responde unas preguntas rápidas. Un asesor revisará tu perfil y te dirá con qué financiera aplicamos.
        </p>
      </div>
      <div className="md:col-span-7">
        <form onSubmit={onSubmit} className="border border-[#3E5641]/10 bg-white p-4 md:p-8" data-testid="buy-form">
          <div className="space-y-5">
            <F label="Nombre completo"><Input required value={form.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="buy-nombre" /></F>
            <div className="grid gap-5 md:grid-cols-2">
              <F label="Teléfono"><Input required inputMode="tel" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="buy-tel" /></F>
              <F label="Correo"><Input type="email" required value={form.correo} onChange={(e) => set("correo", e.target.value)} data-testid="buy-correo" /></F>
            </div>
            <F label="Ciudad"><Input required value={form.ciudad} onChange={(e) => set("ciudad", e.target.value)} data-testid="buy-ciudad" /></F>
            <F label="¿Buscas un auto nuevo o seminuevo?" optional>
              <Choices name="buy-tipo" value={form.tipo_unidad} onChange={(v) => set("tipo_unidad", v)} opts={[["Nuevo", "Nuevo"], ["Seminuevo", "Seminuevo"], ["Sin definir", "Aún no lo sé"]]} />
            </F>
            <F label="¿Ya tienes un auto en mente?">
              <Choices name="buy-mente" value={form.tiene_auto_en_mente} onChange={(v) => set("tiene_auto_en_mente", v)} opts={[["si", "Sí"], ["no", "No"]]} />
            </F>
            {tieneAuto && (
              <div className="rounded-sm border border-[#3E5641]/10 bg-[#F1F3F3]/40 p-4 space-y-4" data-testid="buy-auto-fields">
                <p className="mono text-[10px] uppercase tracking-widest text-[#6B7678]">Datos del auto (referencia)</p>
                <div className="grid gap-4 md:grid-cols-2">
                  <F label="Marca" optional><Input value={form.marca} onChange={(e) => set("marca", e.target.value)} data-testid="buy-auto-marca" /></F>
                  <F label="Año" optional><Input type="number" inputMode="numeric" value={form.anio} onChange={(e) => set("anio", e.target.value)} data-testid="buy-auto-anio" /></F>
                  <F label="Modelo" optional><Input value={form.modelo} onChange={(e) => set("modelo", e.target.value)} data-testid="buy-auto-modelo" /></F>
                  <F label="Versión" optional><Input value={form.version} onChange={(e) => set("version", e.target.value)} data-testid="buy-auto-version" /></F>
                  <F label="Rango de precio" optional><Input value={form.rango_precio} onChange={(e) => set("rango_precio", e.target.value)} placeholder="Ej. 200-250k" data-testid="buy-auto-rango" /></F>
                </div>
                <F label="Transmisión" optional>
                  <Choices name="buy-auto-trans" value={form.transmision} onChange={(v) => set("transmision", v)} opts={["Automática", "Estándar", "CVT"]} />
                </F>
              </div>
            )}
            <button type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-60" data-testid="buy-submit">
              {submitting ? "Iniciando..." : "Iniciar precalificación"} <ArrowRight size={16} className="ml-2 inline" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ================ Paso 2: Precalificación ================ */
const PRE_INITIAL = {
  edad: "", tipo_ingreso: "", ingreso_mensual: "", debe_a_azteca: "",
  buro: "", valor_auto: "", enganche_pct: "", intentos_credito_3m: "0",
};
const OPTS_INGRESO = [
  ["nomina", "Sí — con recibos de nómina"],
  ["actividad_empresarial", "Sí — soy independiente formal (dado de alta en Hacienda)"],
  ["estados_cuenta", "Sí — con estados de cuenta bancarios (informal)"],
  ["ninguno", "No puedo comprobar ingresos"],
];
const OPTS_AZTECA = [
  ["si", "Sí, tengo un adeudo activo"],
  ["no", "No, no debo nada"],
  ["no_cliente", "No soy cliente de Banco Azteca"],
];
const OPTS_BURO = [
  ["bueno", "Buen historial, sin adeudos"],
  ["adeudo_menor", "Tengo un adeudo menor (no con Coppel)"],
  ["adeudo_coppel", "Tengo un adeudo con Coppel"],
  ["adeudos_fuertes", "Tengo adeudos importantes / historial con problemas fuertes"],
  ["sin_historial", "Nunca he tenido un crédito (sin historial)"],
  ["no_se", "No sé cómo está mi buró"],
];

function StepPrecalif({ token, onDone }) {
  const [f, setF, clearDraft] = useDraft(`ladupla_pre_${token}`, PRE_INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const comprueba = ["nomina", "actividad_empresarial", "estados_cuenta"].includes(f.tipo_ingreso);
  const bajoIngreso = comprueba && f.ingreso_mensual && Number(f.ingreso_mensual) < 9000;

  async function onSubmit(e) {
    e.preventDefault();
    const missing = [["tipo_ingreso", "comprobación de ingresos"], ["debe_a_azteca", "Banco Azteca"], ["buro", "buró de crédito"]].find(([k]) => !f[k]);
    if (missing) { toast.error(`Falta responder: ${missing[1]}`); return; }
    setSubmitting(true);
    try {
      await api.patch(`/buyers/by-token/${token}/prequalify`, {
        edad: Number(f.edad), tipo_ingreso: f.tipo_ingreso,
        ingreso_mensual: comprueba ? Number(f.ingreso_mensual || 0) : null,
        debe_a_azteca: f.debe_a_azteca, buro: f.buro,
        valor_auto: Number(f.valor_auto), enganche_pct: Number(f.enganche_pct),
        intentos_credito_3m: Number(f.intentos_credito_3m),
      });
      clearDraft();
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  }

  return (
    <form onSubmit={onSubmit} className="mx-auto max-w-2xl" data-testid="precalif-form">
      <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Precalificación</p>
      <h2 className="mt-3 font-serif text-3xl text-[#3E5641] md:text-4xl">Preguntas rápidas.</h2>
      <p className="mt-3 text-sm text-[#6B7678]">Responde con la verdad: con esto definimos con qué financiera aplicar por ti. Tus respuestas se guardan solas.</p>
      <div className="mt-8 space-y-8">
        <F label="Edad"><Input type="number" inputMode="numeric" min="18" max="99" required value={f.edad} onChange={(e) => set("edad", e.target.value)} data-testid="pre-edad" /></F>
        <F label="¿Puedes comprobar tus ingresos con nómina o estados de cuenta?">
          <Choices name="pre-ingreso" value={f.tipo_ingreso} onChange={(v) => set("tipo_ingreso", v)} opts={OPTS_INGRESO} />
        </F>
        {comprueba && (
          <F label="¿Cuál es tu ingreso mensual aproximado? (MXN)">
            <Input type="number" inputMode="numeric" min="0" step="500" required value={f.ingreso_mensual}
                   onChange={(e) => set("ingreso_mensual", e.target.value.replace(/[^\d.]/g, ""))}
                   placeholder="Ej. 8500" data-testid="pre-monto" />
            {bajoIngreso && (
              <p className="mt-2 rounded-sm bg-[#F1F3F3] p-3 text-xs text-[#6B7678]" data-testid="pre-bajo-ingreso">
                El ingreso mínimo mensual que piden la mayoría de nuestras financieras es de $9,000. Puedes continuar de todas formas: un asesor revisará tu caso.
              </p>
            )}
          </F>
        )}
        <F label="¿Actualmente debes algo a Banco Azteca?">
          <Choices name="pre-azteca" value={f.debe_a_azteca} onChange={(v) => set("debe_a_azteca", v)} opts={OPTS_AZTECA} />
        </F>
        <F label="¿Cómo describirías tu historial en buró de crédito?">
          <Choices name="pre-buro" value={f.buro} onChange={(v) => set("buro", v)} opts={OPTS_BURO} />
        </F>
        <F label="Valor aproximado del auto que buscas financiar (MXN)">
          <Input type="number" inputMode="numeric" min="0" step="1000" required value={f.valor_auto} onChange={(e) => set("valor_auto", e.target.value)} data-testid="pre-valor" />
        </F>
        <F label="¿Qué % de enganche puedes dar?">
          <Input type="number" inputMode="numeric" min="0" max="100" step="1" required value={f.enganche_pct} onChange={(e) => set("enganche_pct", e.target.value)} placeholder="Ej. 20" data-testid="pre-enganche" />
        </F>
        <F label="En los últimos 3 meses, ¿cuántos créditos has intentado meter?">
          <Input type="number" inputMode="numeric" min="0" required value={f.intentos_credito_3m} onChange={(e) => set("intentos_credito_3m", e.target.value)} data-testid="pre-intentos" />
        </F>
      </div>
      <button type="submit" disabled={submitting} className="btn-primary mt-10 w-full disabled:opacity-60" data-testid="pre-submit">
        {submitting ? "Guardando..." : "Continuar con mi solicitud"} <ArrowRight size={16} className="ml-2 inline" />
      </button>
    </form>
  );
}

/* ================ Paso 3: Solicitud completa ================ */
const APP_INITIAL = {
  nombre_completo_ine: "", estado_civil: "", rfc: "", nacionalidad: "Mexicana", lugar_nacimiento: "",
  nivel_estudios: "", dependientes: "0",
  tipo_vivienda: "", pago_vivienda_mensual: "", antiguedad_vivienda: "",
  actividad_economica: "", tipo_actividad: "", nombre_empresa: "", antiguedad_empleo: "",
  direccion_empresa: "", telefono_trabajo: "", dado_alta_hacienda: "no",
  ingreso_mensual_promedio: "", tipo_comprobante: "", fecha_ingreso_empresa: "",
  refs: [emptyRef(), emptyRef(), emptyRef()],
};
const REQUIRED_CHOICES = [
  ["estado_civil", "Estado civil"], ["nivel_estudios", "Nivel de estudios"], ["tipo_vivienda", "Tipo de vivienda"],
  ["tipo_actividad", "Tipo de actividad"], ["tipo_comprobante", "Tipo de comprobante"],
];

function StepSolicitud({ token, onDone }) {
  const [f, setF, clearDraft] = useDraft(`ladupla_app_${token}`, APP_INITIAL);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setRef = (i, k, v) => setF((p) => { const rs = [...p.refs]; rs[i] = { ...rs[i], [k]: v }; return { ...p, refs: rs }; });
  const showPagoVivienda = f.tipo_vivienda === "Rentada" || f.tipo_vivienda === "Hipotecada";

  async function onSubmit(e) {
    e.preventDefault();
    const missing = REQUIRED_CHOICES.find(([k]) => !f[k]);
    if (missing) { toast.error(`Falta responder: ${missing[1]}`); return; }
    const refMissing = f.refs.findIndex((r) => !r.parentesco);
    if (refMissing >= 0) { toast.error(`Referencia ${refMissing + 1}: elige el parentesco`); return; }
    setSaving(true);
    try {
      const { refs, ...rest } = f;
      const { data } = await api.patch(`/buyers/by-token/${token}/application`, {
        ...rest,
        dependientes: Number(f.dependientes || 0),
        pago_vivienda_mensual: showPagoVivienda ? Number(f.pago_vivienda_mensual || 0) : null,
        dado_alta_hacienda: f.dado_alta_hacienda === "si",
        ingreso_mensual_promedio: Number(f.ingreso_mensual_promedio || 0),
        referencias: refs,
      });
      clearDraft();
      onDone(data.whatsapp_link);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }

  return (
    <form onSubmit={onSubmit} className="mx-auto max-w-3xl" data-testid="app-form">
      <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Solicitud de crédito</p>
      <h2 className="mt-3 font-serif text-3xl text-[#3E5641] md:text-4xl">Cuéntanos de ti.</h2>
      <p className="mt-3 text-sm text-[#6B7678]">Tus respuestas se guardan solas en este dispositivo mientras llenas la solicitud.</p>
      <Section title="Datos personales">
        <F label="Nombre completo (como aparece en tu INE)"><Input required value={f.nombre_completo_ine} onChange={(e) => set("nombre_completo_ine", e.target.value)} data-testid="app-nombre-ine" /></F>
        <F label="Estado civil">
          <Choices name="app-estado-civil" value={f.estado_civil} onChange={(v) => set("estado_civil", v)}
            opts={["Soltero/a", "Casado/a Bienes Mancomunados", "Casado/a Bienes Separados", "Viudo/a", "Divorciado/a", "Unión Libre"]} />
        </F>
        <Grid2>
          <F label="RFC con homoclave"><Input required value={f.rfc} onChange={(e) => set("rfc", e.target.value.toUpperCase())} data-testid="app-rfc" /></F>
          <F label="Nacionalidad"><Input required value={f.nacionalidad} onChange={(e) => set("nacionalidad", e.target.value)} data-testid="app-nacionalidad" /></F>
          <F label="Lugar de nacimiento (ciudad y estado)"><Input required value={f.lugar_nacimiento} onChange={(e) => set("lugar_nacimiento", e.target.value)} data-testid="app-lugar-nac" /></F>
          <F label="Dependientes económicos (menores)"><Input type="number" inputMode="numeric" min="0" value={f.dependientes} onChange={(e) => set("dependientes", e.target.value)} data-testid="app-dependientes" /></F>
        </Grid2>
        <F label="Nivel de estudios">
          <Choices name="app-estudios" value={f.nivel_estudios} onChange={(v) => set("nivel_estudios", v)}
            opts={["Preparatoria", "Profesional", "Maestría", "Doctorado", "Técnico", "Otros"]} />
        </F>
      </Section>
      <Section title="Domicilio">
        <F label="Tipo de vivienda">
          <Choices name="app-vivienda" value={f.tipo_vivienda} onChange={(v) => set("tipo_vivienda", v)} opts={["Propia", "Rentada", "Hipotecada", "Con familiares"]} />
        </F>
        <Grid2>
          {showPagoVivienda && (
            <F label="Pago mensual de vivienda"><Input type="number" inputMode="numeric" min="0" required value={f.pago_vivienda_mensual} onChange={(e) => set("pago_vivienda_mensual", e.target.value)} data-testid="app-pago-vivienda" /></F>
          )}
          <F label="Antigüedad viviendo ahí"><Input placeholder="Ej. 3 años 4 meses" required value={f.antiguedad_vivienda} onChange={(e) => set("antiguedad_vivienda", e.target.value)} data-testid="app-antig-vivienda" /></F>
        </Grid2>
      </Section>
      <Section title="Datos laborales / ingresos">
        <F label="Tipo de actividad">
          <Choices name="app-tipo-actividad" value={f.tipo_actividad} onChange={(v) => set("tipo_actividad", v)}
            opts={[["empleado", "Empleado"], ["socio", "Socio de empresa"], ["jubilado", "Jubilado/pensionado"], ["independiente", "Independiente"]]} />
        </F>
        <Grid2>
          <F label="Actividad económica / puesto"><Input required value={f.actividad_economica} onChange={(e) => set("actividad_economica", e.target.value)} data-testid="app-actividad" /></F>
          <F label="Nombre de la empresa/negocio"><Input required value={f.nombre_empresa} onChange={(e) => set("nombre_empresa", e.target.value)} data-testid="app-empresa" /></F>
          <F label="Antigüedad en el empleo"><Input placeholder="Ej. 2 años 5 meses" required value={f.antiguedad_empleo} onChange={(e) => set("antiguedad_empleo", e.target.value)} data-testid="app-antig-empleo" /></F>
          <F label="Teléfono del trabajo (o repite el celular)"><Input required inputMode="tel" value={f.telefono_trabajo} onChange={(e) => set("telefono_trabajo", e.target.value)} data-testid="app-tel-trabajo" /></F>
        </Grid2>
        <F label="Dirección de la empresa (calle, colonia, ciudad, estado, C.P.)"><Textarea rows={2} required value={f.direccion_empresa} onChange={(e) => set("direccion_empresa", e.target.value)} data-testid="app-dir-empresa" /></F>
        <F label="¿Dado de alta en Hacienda?">
          <Choices name="app-hacienda" value={f.dado_alta_hacienda} onChange={(v) => set("dado_alta_hacienda", v)} opts={[["si", "Sí"], ["no", "No"]]} />
        </F>
        <Grid2>
          <F label="Ingreso mensual promedio (MXN)"><Input type="number" inputMode="numeric" min="0" required value={f.ingreso_mensual_promedio} onChange={(e) => set("ingreso_mensual_promedio", e.target.value)} data-testid="app-ingreso" /></F>
          <F label="Fecha de ingreso a la empresa"><Input type="date" required value={f.fecha_ingreso_empresa} onChange={(e) => set("fecha_ingreso_empresa", e.target.value)} data-testid="app-fecha-ingreso" /></F>
        </Grid2>
        <F label="Tipo de comprobante">
          <Choices name="app-comprobante" value={f.tipo_comprobante} onChange={(v) => set("tipo_comprobante", v)} opts={["Recibos de nómina", "Estados de cuenta de banco", "No tengo"]} />
        </F>
      </Section>
      <Section title="Referencias personales (3 obligatorias)">
        {f.refs.map((ref, i) => (
          <div key={i} className="rounded-sm border border-[#3E5641]/10 bg-[#F1F3F3]/40 p-3 space-y-3 md:p-4" data-testid={`ref-${i}`}>
            <p className="mono text-[10px] uppercase tracking-widest text-[#6B7678]">Referencia {i + 1}</p>
            <Grid2>
              <F label="Nombre completo"><Input required value={ref.nombre} onChange={(e) => setRef(i, "nombre", e.target.value)} data-testid={`ref-${i}-nombre`} /></F>
              <F label="Teléfono"><Input required inputMode="tel" value={ref.telefono} onChange={(e) => setRef(i, "telefono", e.target.value)} data-testid={`ref-${i}-tel`} /></F>
            </Grid2>
            <F label="Parentesco">
              <Choices name={`ref-${i}-parentesco`} value={ref.parentesco} onChange={(v) => setRef(i, "parentesco", v)} opts={["Familiar", "Amistad"]} />
            </F>
            <F label="Dirección/domicilio (calle, número, ciudad, estado)"><Input required value={ref.direccion} onChange={(e) => setRef(i, "direccion", e.target.value)} data-testid={`ref-${i}-dir`} /></F>
          </div>
        ))}
      </Section>
      <button type="submit" disabled={saving} className="btn-primary mt-10 w-full disabled:opacity-60" data-testid="app-submit">
        {saving ? "Enviando solicitud..." : "Enviar solicitud"} <ArrowRight size={16} className="ml-2 inline" />
      </button>
    </form>
  );
}

/* ================ Registro ya existente ================ */
function StepDuplicado({ nombre }) {
  return (
    <div className="mx-auto max-w-2xl text-center" data-testid="buy-duplicado">
      <CheckCircle2 size={56} className="mx-auto text-[#C4674F]" />
      <h2 className="mt-6 font-serif text-3xl text-[#3E5641] md:text-4xl">Ya tenemos tu registro{nombre ? `, ${nombre.split(" ")[0]}` : ""}.</h2>
      <p className="mt-6 text-[#6B7678]">
        Ese correo o teléfono ya está registrado con nosotros. Un asesor te va a contactar y te enviará un enlace seguro para continuar donde te quedaste.
      </p>
      <a href={WA_DEFAULT.split("?")[0]} target="_blank" rel="noreferrer" className="btn-primary mt-8 inline-flex w-full justify-center sm:w-auto" data-testid="dup-whatsapp">
        <MessageCircle size={18} className="mr-2" /> Escríbenos por WhatsApp
      </a>
    </div>
  );
}

/* ================ Paso 4: Confirmación ================ */
function StepConfirmacion({ nombre, whatsappLink }) {
  return (
    <div className="mx-auto max-w-2xl text-center" data-testid="buy-confirmacion">
      <CheckCircle2 size={56} className="mx-auto text-[#C4674F]" />
      <p className="mono mt-6 text-xs uppercase tracking-widest text-[#A0553F]">Solicitud enviada</p>
      <h2 className="mt-3 font-serif text-3xl text-[#3E5641] md:text-4xl">Gracias{nombre ? `, ${nombre.split(" ")[0]}` : ""}.</h2>
      <p className="mt-6 text-[#6B7678]">
        Recibimos tu solicitud de crédito. Un asesor de Dupla Automotriz va a revisar tu perfil y te indicará con qué financiera aplicaremos y los siguientes pasos.
      </p>
      <div className="mt-8 rounded-sm border-l-4 border-[#C4674F] bg-[#F1F3F3] p-5 text-left md:p-6">
        <p className="mono text-[10px] uppercase tracking-widest text-[#A0553F]">Siguiente paso</p>
        <p className="mt-2 text-[#3E5641]">Envía tus documentos por WhatsApp para agilizar tu proceso.</p>
      </div>
      <a href={whatsappLink} target="_blank" rel="noreferrer" className="btn-primary mt-8 inline-flex w-full justify-center sm:w-auto" data-testid="conf-whatsapp">
        <MessageCircle size={18} className="mr-2" /> Enviar documentos por WhatsApp
      </a>
      <div className="mt-8">
        <Link to="/" className="text-sm text-[#6B7678] underline hover:text-[#3E5641]" data-testid="conf-home">
          <ArrowLeft size={14} className="mr-1 inline" /> Volver al inicio
        </Link>
      </div>
    </div>
  );
}

/* ================ Helpers ================ */
function emptyRef() { return { nombre: "", telefono: "", direccion: "", parentesco: "" }; }
function F({ label, optional, children }) {
  return (
    <div className="min-w-0">
      <Label className="field-label block leading-snug break-words">{label} {!optional && <span className="text-[#A0553F]">*</span>}</Label>
      {children}
    </div>
  );
}
function Grid2({ children }) { return <div className="grid gap-4 md:grid-cols-2">{children}</div>; }
function Section({ title, children }) {
  return (
    <fieldset className="mt-8 md:mt-10 border-t border-[#3E5641]/10 pt-6 md:pt-8">
      <legend className="mono text-[10px] uppercase tracking-widest text-[#A0553F] px-1">{title}</legend>
      <div className="mt-4 space-y-5">{children}</div>
    </fieldset>
  );
}

/* Tarjetas apiladas, todas visibles, sin menús desplegables */
function Choices({ name, value, onChange, opts }) {
  return (
    <div role="radiogroup" className="mt-2 flex flex-col gap-2" data-testid={name}>
      {opts.map((o) => {
        const [val, lbl] = Array.isArray(o) ? o : [o, o];
        const active = value === val;
        return (
          <button key={val} type="button" role="radio" aria-checked={active} onClick={() => onChange(val)}
            className={`flex w-full min-h-[48px] items-center gap-3 rounded-md border px-4 py-3 text-left text-sm leading-snug transition-colors duration-150 ${active ? "border-[#C4674F] bg-[#C4674F]/10 text-[#3E5641] font-medium" : "border-[#3E5641]/15 bg-white text-[#3E5641] hover:border-[#C4674F]/60"}`}
            data-testid={`${name}-${String(val).replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}>
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${active ? "border-[#C4674F] bg-[#C4674F]" : "border-[#3E5641]/30"}`}>
              {active && <span className="h-2 w-2 rounded-full bg-white" />}
            </span>
            <span className="min-w-0 break-words">{lbl}</span>
          </button>
        );
      })}
    </div>
  );
}
